/**
 * Skillable design tokens, transcribed from the design team's mockups (`ui-mockups/src/index.css`
 * and `ui-mockups/src/muiTheme.js`). The mockups started from the `figma-mui` archetype of the
 * `skillable-engineering/skillable-design-system` repository and its `design.md`, with larger
 * corners and per-scheme shadows. Light mode uses design.md's neutral grays and `#136945`
 * primary; dark mode uses neutral blacks. The screens follow the mockups.
 *
 * This module is the single source of truth for color, type and shape in this app. The MUI
 * theme is built from it, MUI emits the values as CSS custom properties, and Tailwind's
 * `@theme inline` block in `index.css` reads those same properties. Nothing downstream
 * restates a hex value.
 */

/** A color that resolves differently in the light and dark schemes. */
export interface SchemePair {
  readonly light: string
  readonly dark: string
}

/**
 * Picks one side of every {@link SchemePair} in a record.
 *
 * @param pairs - The token record to resolve.
 * @param scheme - Which side of each pair to take.
 * @returns A flat record of resolved color strings.
 */
export function resolveScheme<TKey extends string>(
  pairs: Readonly<Record<TKey, SchemePair>>,
  scheme: 'light' | 'dark',
): Record<TKey, string> {
  const resolved = {} as Record<TKey, string>
  for (const key of Object.keys(pairs) as TKey[]) {
    resolved[key] = pairs[key][scheme]
  }
  return resolved
}

export const primary = {
  main: { light: '#136945', dark: '#00CA72' },
  dark: { light: '#0A3E28', dark: '#00954D' },
  light: { light: '#359679', dark: '#3DF0A0' },
  soft: { light: '#DFF0EB', dark: '#0D2A1D' },
  contrastText: { light: '#FFFFFF', dark: '#04140C' },
  /** Focus and selection rings around primary controls. */
  ring: { light: 'rgba(19, 105, 69, 0.6)', dark: 'rgba(0, 202, 114, 0.45)' },
  /** The soft green glow behind an active tab's label. */
  glow: { light: 'rgba(19, 105, 69, 0.28)', dark: 'rgba(0, 202, 114, 0.35)' },
} as const

export const secondary = {
  main: { light: '#7000FF', dark: '#BE97FF' },
  dark: { light: '#4C00F1', dark: '#A56BFF' },
  light: { light: '#B67DFF', dark: '#E3D0FF' },
  soft: { light: '#F1E6FF', dark: '#1F1536' },
  contrastText: { light: '#FFFFFF', dark: '#141414' },
} as const

export const accent = {
  main: { light: '#00CA72', dark: '#00CA72' },
  dark: { light: '#00954D', dark: '#00954D' },
  light: { light: '#E0FFF1', dark: '#0D2A1D' },
  contrastText: { light: '#141414', dark: '#04140C' },
} as const

/** Dark `main` and `dark` are a step darker than the mockups' #4A4A4A/#333333, so text on it keeps 4.5:1. */
export const softGrey = {
  main: { light: '#E7E7E7', dark: '#333333' },
  dark: { light: '#D2D2D2', dark: '#262626' },
  light: { light: '#F2F2F2', dark: '#676767' },
  translucent: { light: '#F2F2F2', dark: '#676767' },
  contrastText: { light: '#141414', dark: '#FFFFFF' },
} as const

export const hardGrey = {
  main: { light: '#333333', dark: '#FFFFFF' },
  dark: { light: '#141414', dark: '#E7E7E7' },
  light: { light: '#8D8D8D', dark: '#FFFFFF' },
  contrastText: { light: '#FFFFFF', dark: '#141414' },
} as const

export const error = {
  main: { light: '#C62828', dark: '#FF7A80' },
  dark: { light: '#A81F1F', dark: '#E2454D' },
  light: { light: '#EF5350', dark: '#FF8A90' },
  soft: { light: '#FDE8EA', dark: '#2C1215' },
  contrastText: { light: '#FFFFFF', dark: '#141414' },
  /** For error text on a neutral surface, where the `main` tone is too heavy. */
  textOnly: { light: '#C62828', dark: '#FF7A80' },
} as const

/**
 * The estate's fourth severity, sitting between `warning` and `error`. MUI has no slot for it,
 * so it is added by module augmentation in `theme.ts`.
 */
export const critical = {
  main: { light: '#F57C00', dark: '#FF9433' },
  dark: { light: '#F57C00', dark: '#F57C00' },
  light: { light: '#FFCC80', dark: '#FFCC80' },
  soft: { light: '#FFF1E0', dark: '#2C1B09' },
  contrastText: { light: '#141414', dark: '#141414' },
  /** Text and icons on the soft fill or the page, where `main` is too light to read. */
  textOnly: { light: '#A34700', dark: '#FF9433' },
} as const

export const warning = {
  main: { light: '#FFA726', dark: '#FFB74D' },
  dark: { light: '#F57C00', dark: '#F57C00' },
  light: { light: '#FFCC80', dark: '#FFCC80' },
  soft: { light: '#FFF3E0', dark: '#2B2009' },
  contrastText: { light: '#141414', dark: '#141414' },
  /** Text and icons on the soft fill or the page, where `main` is too light to read. */
  textOnly: { light: '#8A4B00', dark: '#FFB74D' },
} as const

export const success = {
  main: { light: '#186F2F', dark: '#4ADE80' },
  dark: { light: '#176B2D', dark: '#22C55E' },
  light: { light: '#4CAF50', dark: '#86EFAC' },
  soft: { light: '#E6F6EA', dark: '#0F2A1A' },
  contrastText: { light: '#FFFFFF', dark: '#04140C' },
} as const

export const background = {
  default: { light: '#FFFFFF', dark: '#141414' },
  /** The raised surface cards, menus and other papers sit on: the mockups' `surface`. */
  paper: { light: '#FFFFFF', dark: '#1E1E1E' },
  /** Slight variance from the page, such as a table header row. */
  contrastXs: { light: '#FBFBFB', dark: '#1A1A1A' },
  contrastSm: { light: '#F2F2F2', dark: '#333333' },
  contrastMd: { light: '#EBEBEB', dark: '#4A4A4A' },
  disabled: { light: '#E7E7E7', dark: '#3D3D3D' },
} as const

export const text = {
  primary: { light: '#141414', dark: '#F5F5F5' },
  secondary: { light: '#4A4A4A', dark: '#D0D0D0' },
  /**
   * The mockups' `text-light`. Mapped onto MUI's `disabled` slot because the archetype's
   * own disabled-button spec uses this value for its label.
   */
  disabled: { light: '#676767', dark: '#B0B0B0' },
} as const

export const divider = { light: '#D2D2D2', dark: '#4A4A4A' } as const

/** The outline of a box, card or panel: 3:1 against the page (WCAG 1.4.11), unlike `divider`. */
export const line = { light: '#8D8D8D', dark: '#6B6B6B' } as const

/**
 * A border that must stand out from the page, such as an idle chip's outline. Light is a shade
 * darker than the mockups' #8D8D8D, which is 2.97:1 on `contrastSm`.
 */
export const borderStrong = { light: '#8A8A8A', dark: '#8D8D8D' } as const

/** An outlined button's hover: accent-light in light mode, the primary green in dark, with a near-black label and border. */
export const outlinedHover = {
  fill: { light: '#E0FFF1', dark: '#00CA72' },
  text: { light: '#141414', dark: '#141414' },
} as const

export const common = {
  white: { light: '#FFFFFF', dark: '#FFFFFF' },
  black: { light: '#141414', dark: '#141414' },
} as const

/** Shadows, which differ per scheme: a card at rest and raised, and an open menu. */
export const elevation = {
  card: {
    light: '0 1px 2px rgba(11, 19, 15, 0.05), 0 4px 14px -6px rgba(11, 19, 15, 0.1)',
    dark: '0 1px 0 rgba(255, 255, 255, 0.03) inset, 0 8px 24px -12px rgba(0, 0, 0, 0.8)',
  },
  cardHover: {
    light: '0 14px 34px -12px rgba(19, 105, 69, 0.35)',
    dark: '0 0 0 1px rgba(0, 202, 114, 0.5), 0 16px 40px -14px rgba(0, 202, 114, 0.45)',
  },
  menu: {
    light: '0 16px 40px -12px rgba(0, 0, 0, 0.45)',
    dark: '0 16px 40px -12px rgba(0, 0, 0, 0.45)',
  },
} as const

/** The mockups' `--hero-gradient`: a mint wash in light, deep brand green in dark. */
export const heroGradient = {
  light: 'linear-gradient(135deg, #DFF0EB 0%, #F2F2F2 55%, #FFFFFF 100%)',
  dark: 'radial-gradient(120% 140% at 0% 0%, #0D3A28 0%, #101A15 45%, #141414 100%)',
} as const

/**
 * The mockups' `--nav-line`, the bar under the app header: primary, accent, primary in light and
 * primary, primary-light, primary in dark. Built from the palette's variables so a site's own
 * primary carries into it.
 */
export const navLine = {
  light:
    'linear-gradient(90deg, var(--tms-palette-primary-main) 0%, var(--tms-palette-accent-main) 50%, var(--tms-palette-primary-main) 100%)',
  dark: 'linear-gradient(90deg, var(--tms-palette-primary-main) 0%, var(--tms-palette-primary-light) 50%, var(--tms-palette-primary-main) 100%)',
} as const

/** Corner radii, in pixels. `focus` is larger so focus rings read as deliberate. */
export const rounded = {
  none: 0,
  default: 8,
  focus: 10,
  /** Cards. */
  xl: 14,
} as const

/** The spacing step. Every `theme.spacing(n)` call resolves to a multiple of this. */
export const spacingBase = 4

/** As the mockups set them; main.tsx loads both from the @fontsource packages. */
export const fontFamily = {
  heading: "'Lora', serif",
  body: "'Inter', sans-serif",
} as const
